/**
 * FoundationHealthPanel.jsx — Step 11 admin tile (minimal, follows
 * the Cost Tracker tile pattern). Polls /api/admin/foundation/health
 * every 30s and surfaces:
 *   • Platform version + schema version
 *   • Infra + Behavioral engine status chips
 *   • Dirty-data spike flag (red when active)
 *   • Self-diagnosis blocked flag
 *   • Escalation queue size
 *   • CHANGELOG entry presence (green if PRD/CHANGELOG mentions current version)
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function StatusChip({ status, label, testid }) {
  const tone =
    status === "ok" || status === true
      ? "bg-emerald-100 text-emerald-800"
      : status === "degraded"
      ? "bg-amber-100 text-amber-800"
      : "bg-red-100 text-red-800";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
      data-testid={testid}
    >
      {label}
    </span>
  );
}

export default function FoundationHealthPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchHealth = async () => {
    try {
      const token =
        localStorage.getItem("admin_token") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/admin/foundation/health`, {
        headers,
      });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 30000);
    return () => clearInterval(id);
  }, []);

  if (error) {
    return (
      <div
        className="p-4 text-sm text-red-600"
        data-testid="foundation-health-error"
      >
        Failed to load foundation health: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="p-4 text-sm text-slate-500"
        data-testid="foundation-health-loading"
      >
        Loading foundation health…
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4" data-testid="foundation-health-panel">
      <div className="flex items-baseline justify-between">
        <h3 className="text-base font-semibold text-slate-900">
          Foundation Health
        </h3>
        <span className="text-[11px] text-slate-500">
          v{data.platform_version} · schema {data.schema_version}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div
          className="rounded-lg border border-slate-200 bg-white p-3"
          data-testid="foundation-engine-statuses"
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Engines
          </div>
          <div className="flex flex-col gap-1">
            <StatusChip
              status={data.infra_engine_status}
              label={`Infra: ${data.infra_engine_status}`}
              testid="foundation-infra-status"
            />
            <StatusChip
              status={data.behavioral_engine_status}
              label={`Behavioral: ${data.behavioral_engine_status}`}
              testid="foundation-behavioral-status"
            />
          </div>
        </div>

        <div
          className="rounded-lg border border-slate-200 bg-white p-3"
          data-testid="foundation-dirty-data"
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Dirty data
          </div>
          <StatusChip
            status={!data.dirty_data_spike}
            label={data.dirty_data_spike ? "SPIKE active" : "Clean"}
            testid="foundation-dirty-data-chip"
          />
          {data.self_diagnosis_blocked ? (
            <div
              className="mt-2 text-[11px] text-amber-700"
              data-testid="foundation-self-diagnosis-blocked"
            >
              Self-diagnosis blocked
            </div>
          ) : null}
        </div>

        <div
          className="rounded-lg border border-slate-200 bg-white p-3"
          data-testid="foundation-queue"
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Escalations
          </div>
          <div className="text-xl font-semibold text-slate-900">
            {data.escalation_queue_size}
          </div>
          <div className="text-[11px] text-slate-500">queued</div>
        </div>

        <div
          className="rounded-lg border border-slate-200 bg-white p-3"
          data-testid="foundation-changelog"
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            CHANGELOG
          </div>
          <StatusChip
            status={data.changelog_entry_present}
            label={
              data.changelog_entry_present ? "Entry present" : "Missing entry"
            }
            testid="foundation-changelog-chip"
          />
        </div>
      </div>
    </div>
  );
}
